import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule
} from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FlexLayoutModule } from '@angular/flex-layout';
import { BidiModule } from '@angular/cdk/bidi';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import {
  AccordionAnchorDirective,
  AccordionDirective,
  AccordionLinkDirective,
  AdminLayoutComponent,
  AuthLayoutComponent,
  HeaderComponent,
  MenuComponent,
  NotificationComponent,
  OptionsComponent,
  SidebarComponent
} from './core';
import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import { AuthService } from './auth/auth.service';
import { AuthGuard } from './auth/auth.guard';
import { SidebarService } from './main/common/menu/service/sidebar.service';
import { PanelMenuModule } from 'primeng/panelmenu';
import { SiteResolver } from './main/common/site/site.resolver';
import { SiteService } from './main/common/site/service/site.service';
import { LogoBiggerModule } from './shared/custom-components/logo-bigger/logo-bigger.module';
import { BaseAPIService } from './base-api.service';
import { HttpInterceptorService } from './shared/services/http-interceptor.service';
import {
  SchedulerMonitorReportComponent
} from './main/scheduler/component/schedule-manager/component/scheduler-monitor-report/scheduler-monitor-report.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import {
  MonitorEventComponent
} from './main/scheduler/component/schedule-manager/component/scheduler-monitor-report/component/monitor-event/monitor-event.component';
import { BiggerPictureMessagesService } from './shared/services/bigger-picture-messages.service';
import { CustomFormModule } from './shared/custom-components/custom-form/custom-form.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MAT_DIALOG_DATA,
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { UiService } from './shared/services/ui.service';
import { DialogService } from './shared/services/dialog-service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BlockUIModule } from 'ng-block-ui';
import { MessageService } from 'primeng/api';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { SalesMonitorGuard } from './main/sales-monitor/sales-monitor.guard';
import { NgxBpRefreshModule } from '@biggerpicture/ngx-bp-refresh';
import { RouteGuard } from './auth/route.guard';
import { NgxBpChangePasswordModule } from '@biggerpicture/ngx-bp-change-password';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatNativeDateModule } from '@angular-material-components/datetime-picker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ListMenuItemsComponent } from './main/list-menu-items/list-menu-items.component';
import { HomepageComponent } from './main/homepage/homepage.component';

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export class MissingTranslationHelper implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams): string {
    if (params.interpolateParams) {
      return params.translateService.parser.interpolate(
        (params.interpolateParams as any).default || params.key,
        params.interpolateParams
      );
    }
    return params.key;
  }
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
  wheelSpeed: 2,
  wheelPropagation: true,
  minScrollbarLength: 20
};

export class DynamicLocaleId extends String {
  constructor(protected uiService: UiService) {
    super('');
  }

  toString(): string {
    return this.uiService.locale;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SidebarComponent,
    NotificationComponent,
    OptionsComponent,
    MenuComponent,
    AdminLayoutComponent,
    AuthLayoutComponent,
    AccordionAnchorDirective,
    AccordionLinkDirective,
    AccordionDirective,
    SchedulerMonitorReportComponent,
    MonitorEventComponent,
    HomepageComponent,
    ListMenuItemsComponent,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    BlockUIModule.forRoot(),
    ScrollingModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      extendedTimeOut: 1,
      positionClass: 'toast-top-center',
      progressBar: false,
      progressAnimation: 'increasing',
      preventDuplicates: true,
    }),
    ConfirmDialogModule,
    RouterModule.forRoot(AppRoutes, {relativeLinkResolution: 'legacy'}),
    FormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      missingTranslationHandler: {provide: MissingTranslationHandler, useClass: MissingTranslationHelper},
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    LoadingBarRouterModule,
    MatSidenavModule,
    MatCardModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatTabsModule,
    MatListModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatProgressBarModule,
    MtxNativeDatetimeModule,
    MtxDatetimepickerModule,
    FlexLayoutModule,
    BidiModule,
    // AgmCoreModule.forRoot({apiKey: 'AIzaSyB3HQ_Gk_XRt6KitPdiHQNGpVn0NDwQGMI'}),
    PerfectScrollbarModule,
    PanelMenuModule,
    LogoBiggerModule,
    MatDatepickerModule,
    NgxMatNativeDateModule,
    MatNativeDateModule,
    MatInputModule,
    CustomFormModule,
    MatDialogModule,
    ConfirmDialogModule,
    HammerModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    DragDropModule,
    NgxBpRefreshModule,
    NgxBpChangePasswordModule,
    ProgressSpinnerModule,
  ],
  providers: [
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    DatePipe,
    MatDialog,
    BaseAPIService,
    AuthService,
    AuthGuard,
    RouteGuard,
    SalesMonitorGuard,
    SidebarService,
    SiteService,
    UiService,
    DialogService,
    MessageService,
    BiggerPictureMessagesService,
    SiteResolver,
    {provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true},
    {
      provide: MAT_DATE_LOCALE,
      deps: [UiService],
      useFactory: (uiService) => uiService.culture
    },
    {
      provide: LOCALE_ID,
      deps: [UiService],
      useClass: DynamicLocaleId
    },
    {provide: MatDialogRef, useValue: {}},
    {provide: MAT_DIALOG_DATA, useValue: []},
    {provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {hasBackdrop: false}},
    DecimalPipe
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {
}
